import React from 'react';
import { Link } from 'react-router-dom';
import { Coins } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useLeadBalance } from '@/lib/creditsBus';
import { canBuyLeads } from '@/lib/roles';

/**
 * PERSISTENT LEAD CREDIT BALANCE — header pill.
 *
 * Shown for signed-in contractor and subcontractor accounts only (homeowners
 * and admins never buy leads). The number is ALWAYS the server's:
 * useLeadBalance() re-reads getLeadBalance() — the single source of truth — on
 * mount, on every creditsChanged() announcement (lead purchase, credit pack,
 * unlock, membership purchase/renewal/cancel), on tab focus and on cross-tab
 * storage events. Nothing here is ever computed locally.
 *
 * Clicking opens the membership page, which shows the same balance broken down
 * into plan credits, purchased credits, usage and the full ledger.
 */
const HeaderCreditPill: React.FC<{ compact?: boolean; onNavigate?: () => void }> = ({
  compact,
  onNavigate,
}) => {
  const { path, lang } = useI18n();
  const { profile } = useAuth();
  const eligible = !!profile && canBuyLeads(profile.user_type);
  const { balance, loading } = useLeadBalance(eligible ? profile?.id : undefined);

  if (!eligible) return null;

  const es = lang === 'es';
  const n = balance.spendable;
  const tone =
    n > 0
      ? 'border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100'
      : 'border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100';

  return (
    <Link
      to={path('/membership')}
      onClick={onNavigate}
      title={
        es
          ? `${n} crédito(s) disponible(s) · ver membresía`
          : `${n} credit(s) available · view membership`
      }
      aria-label={es ? `${n} créditos de lead disponibles` : `${n} lead credits available`}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-bold transition-colors ${tone} ${
        compact ? 'w-full justify-center' : ''
      }`}
    >
      <Coins className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span className="tabular-nums">{loading ? '—' : n}</span>
      <span className="text-xs font-semibold uppercase tracking-wide opacity-80">
        {es ? 'créditos' : n === 1 ? 'credit' : 'credits'}
      </span>
    </Link>
  );
};

export default HeaderCreditPill;
